import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { coverageStatusColor, coverageStatusLabel } from '@/shared/lib/labels';
import { COVERAGE_STATUSES, type CoverageStatus } from '@/shared/model/enums';
import { Async } from '@/shared/ui/Async';
import { Chip } from '@/shared/ui/Chip';
import { MapView, type MapPin } from '@/shared/ui/MapView';

import { useMapMasjids, useMapRequests } from '../hooks/use-admin';

const MASJID_PIN_COLOR = 'var(--color-on-surface-muted)';

/**
 * Phase 5 (D-013) admin map: masjids + coverage requests (any status), color-coded
 * by request status. Status filter chips let admins narrow the view. Reads via
 * adminRepository (repository pattern — no direct supabase calls here).
 */
export function AdminMapRoute(): JSX.Element {
  const { t } = useTranslation();
  const masjids = useMapMasjids();
  const requests = useMapRequests();
  const [visibleStatuses, setVisibleStatuses] = useState<Set<CoverageStatus>>(
    () => new Set(COVERAGE_STATUSES),
  );

  const requestPins = useMemo<MapPin[]>(() => {
    return (requests.data ?? [])
      .filter((r) => visibleStatuses.has(r.status))
      .map((r) => ({
        id: `request-${r.id}`,
        lat: r.latitude,
        lng: r.longitude,
        title: r.masjidName,
        body: `${coverageStatusLabel(t, r.status)} · ${r.startDate}${
          r.startDate === r.endDate ? '' : ` → ${r.endDate}`
        }`,
        color: coverageStatusColor(r.status),
      }));
  }, [requests.data, visibleStatuses, t]);

  const masjidPins = useMemo<MapPin[]>(() => {
    return (masjids.data ?? []).map((m) => ({
      id: `masjid-${m.id}`,
      lat: m.latitude,
      lng: m.longitude,
      title: m.name,
      body: m.city ?? undefined,
      color: MASJID_PIN_COLOR,
    }));
  }, [masjids.data]);

  function toggleStatus(s: CoverageStatus): void {
    setVisibleStatuses((prev) => {
      const next = new Set(prev);
      if (!next.delete(s)) next.add(s);
      return next;
    });
  }

  return (
    <main className="page stack">
      <h1>{t('admin.mapTitle')}</h1>
      <p className="muted">
        {t('admin.mapLegend', {
          masjids: masjids.data?.length ?? 0,
          requests: requests.data?.length ?? 0,
        })}
      </p>

      <div className="wrap">
        {COVERAGE_STATUSES.map((s) => (
          <Chip
            key={s}
            label={coverageStatusLabel(t, s)}
            selected={visibleStatuses.has(s)}
            onToggle={() => toggleStatus(s)}
          />
        ))}
      </div>

      <Async query={masjids}>
        {() => (
          <Async query={requests}>
            {() => <MapView pins={[...masjidPins, ...requestPins]} height={560} />}
          </Async>
        )}
      </Async>
    </main>
  );
}
